import { DrawerForm, DrawerSizeEnum, StopPointsEnum } from '@acceso/design-system';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormTypeEnum } from '@/types';

import { useProgramDrawerFormState } from '../../hooks/useProgramDrawerFormState';
import { TProgram, TProgramForm, TEnrichments } from '../../types';

import { ProgramDrawer } from './ProgramDrawer';
import { ProgramScheduleListDrawer } from './ProgramScheduleListDrawer';

export type TProgramDrawerFormProps = {
  isOpen: boolean;
  data?: {
    type: FormTypeEnum;
    data?: TProgram | null;
  };
  onClose: () => void;
  onSubmit: (data: TProgramForm) => void;
  isMutating: boolean;
};

export const ProgramDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TProgramDrawerFormProps) => {
  const { t } = useTranslation();
  const { form, PROGRAM_FORM_DEFAULT_VALUES } = useProgramDrawerFormState();

  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    if (data?.data) {
      setIsInitializing(true);

      const { channel, ...rest } = data.data;
      const link_enrichments = rest.parameters.link_with_channel_enrichments;
      const ch: Partial<TEnrichments> = channel.parameters?.enrichments ?? {};

      const mergeVal = (chVal: boolean | undefined, prgVal: boolean | undefined) =>
        link_enrichments ? (chVal === true ? true : (prgVal ?? false)) : (prgVal ?? false);

      form.reset({
        ...rest,
        channel_id: channel.id,
        start_datetime: new Date(rest.start_datetime),
        end_datetime: new Date(rest.end_datetime),
        parameters: {
          ...rest.parameters,
          enrichments: {
            stt: mergeVal(ch.stt, rest.parameters.enrichments.stt),
            logos: mergeVal(ch.logos, rest.parameters.enrichments.logos),
            peoples: mergeVal(ch.peoples, rest.parameters.enrichments.peoples),
            ocr: mergeVal(ch.ocr, rest.parameters.enrichments.ocr),
            advertising: mergeVal(ch.advertising, rest.parameters.enrichments.advertising),
          },
          link_with_channel_enrichments: rest.parameters.link_with_channel_enrichments ?? true,
          ai_clips_require_review: rest.parameters.ai_clips_require_review ?? true,
        },
      });
      setTimeout(() => setIsInitializing(false), 0);
    }
  }, [data]);

  const handleClose = () => {
    form.reset(PROGRAM_FORM_DEFAULT_VALUES);
    onClose();
  };

  const handleSubmit = form.handleSubmit(newData => {
    onSubmit(newData);
  });

  return (
    <DrawerForm
      title={
        !data?.data
          ? t('Crear nuevo programa')
          : data.type === FormTypeEnum.UPDATE
            ? t('Editar programa')
            : t('Duplicar programa')
      }
      isOpen={isOpen}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      isMutating={isMutating}
      initialWidth={StopPointsEnum.LARGE}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
    >
      <div className="flex w-full gap-5">
        <ProgramDrawer form={form} isInitializing={isInitializing} />
        <ProgramScheduleListDrawer form={form} />
      </div>
    </DrawerForm>
  );
};
